import { Injectable, inject, signal } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import {
  SWITCH_LANDING_KEY,
  parseSwitchLanding,
  serialiseSwitchLanding,
  type SwitchLanding,
} from './switch-landing.util';

/**
 * SwitchLandingService — carries a customer switch's verdict across the
 * reload and hands the shell one notice to render.
 *
 * RESPONSIBILITY: `PlatformCustomerService.switchTo` parks a landing in
 * sessionStorage when the API reports the target holds no employee row for
 * the caller, then hard-reloads. This service reads that flag ONCE at
 * construction (the shell root injects it, so that is app boot), drops it
 * when it no longer describes the active session, and exposes the survivor
 * as `notice`. `dismiss()` clears it for good.
 *
 * WHY sessionStorage: the switch sweeps every customer-scoped localStorage
 * key and purges the persisted Apollo cache, so neither of those can carry
 * the flag. sessionStorage is untouched by the sweep and dies with the tab.
 *
 * DROP RULES (all in `readParked`):
 *   - malformed, unknown role state or older than 24 h → dropped
 *   - `customer-id` in localStorage differs from the parked customer → dropped
 *     (a second switch, or a sign-in as someone else, made it stale)
 *   - storage throws (private mode, quota, disabled) → treated as absent
 *
 * AI CONTEXT: `notice` is only ever set from the constructor and cleared by
 * `dismiss()`; nothing else writes it. `park()` is the one writer of the
 * key and is called by the switch immediately before `reloadApp`.
 */
@Injectable({ providedIn: 'root' })
export class SwitchLandingService {
  private readonly analytics = inject(AnalyticsService);

  /** The parked landing this boot found, or null. Kept off the signal so
   *  `dismiss()` never reads the signal it is about to write. */
  private landing: SwitchLanding | null = null;

  private readonly _notice = signal<SwitchLanding | null>(null);
  /** The landing the shell should show, or null. */
  readonly notice = this._notice.asReadonly();

  constructor() {
    this.landing = this.readParked();
    if (this.landing) {
      this._notice.set(this.landing);
      this.analytics.capture(ANALYTICS_EVENTS.SWITCH_LANDING_SHOWN, {
        customer_id: this.landing.customerId,
        role_state: this.landing.roleState,
        role_count: this.landing.roleCount,
      });
    }
  }

  /** Park a landing for the shell that the imminent reload boots. */
  park(landing: SwitchLanding): void {
    try {
      sessionStorage.setItem(SWITCH_LANDING_KEY, serialiseSwitchLanding(landing));
    } catch {
      /* storage unavailable — the notice is best-effort, the switch is not */
    }
  }

  /** Clear the notice and the parked key; fires the dismissal event once. */
  dismiss(): void {
    const current = this.landing;
    if (!current) return;
    this.landing = null;
    this._notice.set(null);
    this.clearParked();
    this.analytics.capture(ANALYTICS_EVENTS.SWITCH_LANDING_DISMISSED, {
      customer_id: current.customerId,
    });
  }

  private readParked(): SwitchLanding | null {
    try {
      const raw = sessionStorage.getItem(SWITCH_LANDING_KEY);
      if (raw === null) return null;
      const parsed = parseSwitchLanding(raw, Date.now());
      if (!parsed || parsed.customerId !== localStorage.getItem('customer-id')) {
        // Stale, malformed or describing a customer this session is no
        // longer in — a notice about the wrong customer is worse than none.
        this.clearParked();
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private clearParked(): void {
    try {
      sessionStorage.removeItem(SWITCH_LANDING_KEY);
    } catch {
      /* storage unavailable — nothing was parked to clear */
    }
  }
}
